import { useState } from 'react';
import { useTenants, useUpdateTenant, useCreateTenant } from '@/hooks/useAdmin';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Building2, Eye, Plus, MoreHorizontal, Pencil, ToggleLeft } from 'lucide-react';
import { toast } from 'sonner';
import { setImpersonatedTenant } from '@/lib/api';
import type { Tenant } from '@/types/models';

interface OrgFormState {
  name: string;
  slug: string;
  features: Record<string, boolean>;
}

const EMPTY_FORM: OrgFormState = {
  name: '',
  slug: '',
  features: { crm_enabled: false, vapi_enabled: false },
};

export default function TenantsPage() {
  const { data: tenants = [], isLoading } = useTenants();
  const updateTenant = useUpdateTenant();
  const createTenant = useCreateTenant();
  const { user } = useAuth();
  const { impersonatedTenantId, setImpersonatedTenantId } = useApp();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<OrgFormState>(EMPTY_FORM);

  const isSuperAdmin = user?.is_super_admin ?? false;

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (tenant: Tenant) => {
    setEditingId(tenant.id);
    setForm({
      name: tenant.name,
      slug: tenant.slug,
      features: { ...tenant.features },
    });
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!form.name.trim() || !form.slug.trim()) {
      toast.error('Name and slug are required');
      return;
    }

    if (editingId) {
      updateTenant.mutate(
        { id: editingId, data: { name: form.name, slug: form.slug, features: form.features as Tenant['features'] } },
        {
          onSuccess: () => { toast.success('Organization updated'); setDialogOpen(false); },
          onError: () => toast.error('Failed to update organization'),
        },
      );
    } else {
      createTenant.mutate(
        { name: form.name, slug: form.slug, features: form.features as Tenant['features'], is_active: true },
        {
          onSuccess: () => { toast.success('Organization created'); setDialogOpen(false); },
          onError: () => toast.error('Failed to create organization'),
        },
      );
    }
  };

  const handleFeatureToggle = (tenantId: string, feature: string, value: boolean) => {
    const tenant = tenants.find(t => t.id === tenantId);
    if (!tenant) return;
    updateTenant.mutate(
      { id: tenantId, data: { features: { ...tenant.features, [feature]: value } } },
      {
        onSuccess: () => toast.success(`${feature.replace(/_/g, ' ')} ${value ? 'enabled' : 'disabled'}`),
      },
    );
  };

  const handleToggleActive = (tenant: Tenant) => {
    updateTenant.mutate(
      { id: tenant.id, data: { is_active: !tenant.is_active } },
      {
        onSuccess: () => toast.success(`${tenant.name} ${tenant.is_active ? 'deactivated' : 'activated'}`),
      },
    );
  };

  const handleImpersonate = (tenantId: string) => {
    setImpersonatedTenantId(tenantId);
    setImpersonatedTenant(tenantId);
    queryClient.invalidateQueries();
    toast.success(`Now viewing as: ${tenants.find(t => t.id === tenantId)?.name}`);
  };

  const handleClearImpersonation = () => {
    setImpersonatedTenantId(null);
    setImpersonatedTenant(null);
    queryClient.invalidateQueries();
    toast.success('Returned to your organization');
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Organizations" subtitle="Manage organizations and their feature access">
        <div className="flex items-center gap-2">
          {impersonatedTenantId && (
            <Button variant="destructive" size="sm" onClick={handleClearImpersonation}>
              Stop Viewing As
            </Button>
          )}
          {isSuperAdmin && (
            <Button size="sm" className="gap-1.5" onClick={openCreate}>
              <Plus className="h-4 w-4" /> New Organization
            </Button>
          )}
        </div>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All Organizations</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Slug / Subdomain</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Features</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading...</TableCell>
                </TableRow>
              ) : tenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No organizations found</TableCell>
                </TableRow>
              ) : (
                tenants.map(tenant => (
                  <TableRow key={tenant.id} className={impersonatedTenantId === tenant.id ? 'bg-primary/5' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{tenant.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{tenant.slug}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={tenant.is_active ? 'default' : 'secondary'}>
                        {tenant.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-3">
                        {Object.entries(tenant.features).map(([key, val]) => (
                          <div key={key} className="flex items-center gap-1.5">
                            <Switch
                              checked={val}
                              onCheckedChange={(v) => handleFeatureToggle(tenant.id, key, v)}
                              className="scale-75"
                            />
                            <span className="text-xs capitalize">{key.replace(/_enabled/g, '').replace(/_/g, ' ')}</span>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleImpersonate(tenant.id)} className="gap-2">
                            <Eye className="h-4 w-4" /> View As
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEdit(tenant)} className="gap-2">
                            <Pencil className="h-4 w-4" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleActive(tenant)} className="gap-2">
                            <ToggleLeft className="h-4 w-4" />
                            {tenant.is_active ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Organization' : 'New Organization'}</DialogTitle>
            <DialogDescription>
              {editingId ? 'Update organization details and feature access.' : 'Create a new organization on the platform.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name</Label>
              <Input
                id="org-name"
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setForm(prev => ({
                    ...prev,
                    name,
                    ...(!editingId ? { slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') } : {}),
                  }));
                }}
                placeholder="Acme Recycling"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-slug">Slug (subdomain)</Label>
              <div className="flex items-center gap-1">
                <Input
                  id="org-slug"
                  value={form.slug}
                  onChange={(e) => setForm(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                  placeholder="acme"
                  className="flex-1"
                />
                
              </div>
            </div>
            <div className="space-y-2">
              <Label>Feature Toggles</Label>
              <div className="space-y-3">
                {Object.entries(form.features).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{key.replace(/_enabled/g, '').replace(/_/g, ' ')}</span>
                    <Switch
                      checked={val}
                      onCheckedChange={(v) => setForm(prev => ({ ...prev, features: { ...prev.features, [key]: v } }))}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createTenant.isPending || updateTenant.isPending}>
              {editingId ? 'Save Changes' : 'Create Organization'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
